// Miteigentümer & Vertreter und Zustellung (Handoff Eigentümer §7, E8). Reine Funktionen – testbar.

import { emailGueltig } from '../immobilien/portal';

export type BeteiligtenTyp = 'miteigentuemer' | 'vertreter';
export type MitArt = 'miteigentum' | 'erbengemeinschaft' | 'ehepartner';
export type VertreterArt = 'vorsorgevollmacht' | 'generalvollmacht' | 'betreuer' | 'testamentsvollstrecker' | 'geschaeftsfuehrer' | 'etv_vollmacht';

export const MIT_ARTEN: readonly { key: MitArt; label: string }[] = [
  { key: 'miteigentum', label: 'Miteigentum' }, { key: 'erbengemeinschaft', label: 'Erbengemeinschaft' }, { key: 'ehepartner', label: 'Ehepartner' },
];
export const VERTRETER_ARTEN: readonly { key: VertreterArt; label: string }[] = [
  { key: 'vorsorgevollmacht', label: 'Vorsorgevollmacht' }, { key: 'generalvollmacht', label: 'Generalvollmacht' }, { key: 'betreuer', label: 'Betreuer' },
  { key: 'testamentsvollstrecker', label: 'Testamentsvollstrecker' }, { key: 'geschaeftsfuehrer', label: 'Geschäftsführer' }, { key: 'etv_vollmacht', label: 'ETV-Vollmacht' },
];
export const artLabel = (art: string): string => [...MIT_ARTEN, ...VERTRETER_ARTEN].find(a => a.key === art)?.label ?? art;

export interface Beteiligter {
  id: string;
  typ: BeteiligtenTyp;
  art: string;
  name: string;
  email: string | null;
  telefon: string | null;
  anteil: number | null;
  zustellvertreter: boolean;
  umfang: string | null;
  gueltig_bis: string | null;
  dokument_id: string | null;
}

/** Anteil des Haupteigentümers: 100 % minus Anteile der Miteigentümer (nie unter 0). */
export function eigenerAnteil(b: readonly Pick<Beteiligter, 'typ' | 'anteil'>[]): number {
  const summe = b.filter(x => x.typ === 'miteigentuemer').reduce((s, x) => s + (x.anteil ?? 0), 0);
  return Math.max(0, Math.round((100 - summe) * 100) / 100);
}

/** Zustellvertreter fehlt, wenn es Miteigentümer gibt, aber niemand benannt ist (§ 25 Abs. 2 WEG). */
export function zustellvertreterFehlt(b: readonly Pick<Beteiligter, 'typ' | 'zustellvertreter'>[]): boolean {
  return b.some(x => x.typ === 'miteigentuemer') && !b.some(x => x.zustellvertreter);
}

/** Vollmacht läuft (ohne Ende oder Ende ≥ heute). */
export const vollmachtGueltig = (b: Pick<Beteiligter, 'gueltig_bis'>, heute: string): boolean => !b.gueltig_bis || b.gueltig_bis.slice(0, 10) >= heute;

export interface BeteiligtenEntwurf { typ: BeteiligtenTyp; art: string; name: string; email: string; anteil: string; umfang: string; gueltigBis: string }

export function beteiligtenFehler(e: BeteiligtenEntwurf, bisher: readonly Pick<Beteiligter, 'typ' | 'anteil'>[], heute: string): string | null {
  if (!e.name.trim()) return 'Name fehlt';
  if (e.email.trim() && !emailGueltig(e.email)) return 'E-Mail prüfen';
  if (e.typ === 'miteigentuemer' && e.anteil.trim()) {
    const a = Number(e.anteil.replace(',', '.'));
    if (!Number.isFinite(a) || a <= 0 || a >= 100) return 'Anteil zwischen 0 und 100 %';
    if (eigenerAnteil([...bisher, { typ: 'miteigentuemer', anteil: a }]) <= 0) return 'Die Anteile der Miteigentümer ergeben 100 % oder mehr';
  }
  if (e.typ === 'vertreter' && e.gueltigBis && e.gueltigBis < heute) return '„Gültig bis“ liegt in der Vergangenheit';
  return null;
}

export function beteiligtenPayload(eigId: string, e: BeteiligtenEntwurf): Record<string, string | number | null> {
  const leer = (s: string) => (s.trim() ? s.trim() : null);
  const anteil = e.typ === 'miteigentuemer' && e.anteil.trim() ? Math.round(Number(e.anteil.replace(',', '.')) * 100) / 100 : null;
  return {
    eigentuemer_id: eigId, typ: e.typ, art: e.art, name: e.name.trim(), email: leer(e.email), anteil,
    umfang: e.typ === 'vertreter' ? leer(e.umfang) : null, gueltig_bis: e.typ === 'vertreter' && e.gueltigBis ? e.gueltigBis : null,
  };
}

// ── Zustellung ─────────────────────────────────────────────────────────────────

export type Versandweg = 'post' | 'email' | 'portal';
export const VERSANDWEGE: readonly { key: Versandweg; label: string }[] = [
  { key: 'post', label: 'Post' }, { key: 'email', label: 'E-Mail' }, { key: 'portal', label: 'Portal' },
];
export type PostArt = 'etv' | 'abrechnung' | 'mahnung';
export const POST_ARTEN: readonly { key: PostArt; label: string }[] = [
  { key: 'etv', label: 'ETV-Einladung & Protokoll' }, { key: 'abrechnung', label: 'Abrechnungen' }, { key: 'mahnung', label: 'Mahnungen & Fristen' },
];

/** Gespeicherter Versandweg, sonst Vorschlag: Portal bei aktivem Zugang, E-Mail wenn vorhanden, sonst Post. */
export function versandweg(gespeichert: unknown, portalAktiv: boolean, email: string): { weg: Versandweg; gespeichert: boolean } {
  if (gespeichert === 'post' || gespeichert === 'email' || gespeichert === 'portal') return { weg: gespeichert, gespeichert: true };
  return { weg: portalAktiv ? 'portal' : email ? 'email' : 'post', gespeichert: false };
}

/** „immer per Post“ je Dokumentart; Mahnungen standardmäßig per Post (Zugangsnachweis). */
export function postPflicht(roh: unknown): Record<PostArt, boolean> {
  const o = roh && typeof roh === 'object' ? roh as Record<string, unknown> : {};
  const lies = (k: PostArt, standard: boolean) => (typeof o[k] === 'boolean' ? o[k] as boolean : standard);
  return { etv: lies('etv', false), abrechnung: lies('abrechnung', false), mahnung: lies('mahnung', true) };
}

/** Ohne Einwilligung zur Textform müssen fristgebundene Schreiben zusätzlich per Post gehen. */
export const textformWarnung = (weg: Versandweg, einwilligung: string | null): boolean => weg !== 'post' && !einwilligung;

export interface ZustellAdresse { co: string; strasse: string; hausnummer: string; plz: string; stadt: string }

export const hatAbweichendeAdresse = (a: ZustellAdresse): boolean => !!(a.strasse.trim() || a.plz.trim() || a.stadt.trim());

export function adressText(a: Omit<ZustellAdresse, 'co'> & { co?: string }): string {
  return [a.co?.trim() ? `c/o ${a.co.trim()}` : '', [a.strasse, a.hausnummer].map(x => x.trim()).filter(Boolean).join(' '), [a.plz, a.stadt].map(x => x.trim()).filter(Boolean).join(' ')]
    .filter(Boolean).join(', ');
}
